import { getKennzahl, Kennzahl } from '../strukturiert/Kennzahl';
import { KennzahlFaktor } from '../strukturiert/KennzahlFaktor';

export function getFactorHochrechnung(zeitraum_von: number, zeitraum_bis: number, jahr: number, extraObj = false): any {
    const date_bis = new Date(+zeitraum_bis * 1000);
    const date_bis2 = new Date(date_bis.getFullYear(), date_bis.getMonth(), date_bis.getDate());
    zeitraum_bis = date_bis2.getTime() / 1000;
    const jahr_tage = jahr % 4 === 0 ? 366 : 365;
    const zeitraum_tage = Math.round((zeitraum_bis - zeitraum_von) / 86400);
    let factor_hochrechnung = jahr_tage / (zeitraum_tage + 1);
    factor_hochrechnung = Math.round(factor_hochrechnung * 100) / 100;
    if (extraObj) {
        return { factor_hochrechnung, jahr_tage, zeitraum_tage };
    }
    return factor_hochrechnung;
}

export type ParametersSetKennzahl = {
    standort_tid: string,
    data: any, // Globaldata
    kennName: string,
    bereich: string,
    key: string,
    field: string,
    item: any, // dataRow
    einheit: string,
    einheitOrig?: string,
    valueOrig?: number,
    gesamtKey?: string,
    gesamtName?: string,
    faktor_um?: number,
    faktor_um_einheit?: string,
    faktor_um_quelle?: string,
    faktor_um_info?: string;
    faktor_co2?: number,
    faktor_co2_einheit?: string,
    faktor_co2_quelle?: string,
    setHochrechnung?: boolean,
    hidden?: boolean,
    formel_um?: string,
    formel_co2?: string,
    formel_hoch?: string,
    faktoren?: Array<any> // Custom-Faktoren
}

export function setKennzahlObj(params: ParametersSetKennzahl): Kennzahl {
    const { standort_tid, data, kennName, bereich, key, field, item, einheit, einheitOrig, valueOrig, gesamtKey, gesamtName,
        faktor_um, faktor_um_einheit, faktor_um_quelle, faktor_co2, faktor_co2_einheit, faktor_co2_quelle, setHochrechnung, hidden,
        formel_um, formel_co2, formel_hoch, faktor_um_info, faktoren } = params;
    return setKennzahl(standort_tid, data, kennName, bereich, key, field, item, einheit, einheitOrig, gesamtKey, gesamtName,
        faktor_um, faktor_um_quelle, faktor_co2, faktor_co2_quelle, faktor_co2_einheit, setHochrechnung, faktor_um_einheit, hidden,
        formel_um, formel_co2, formel_hoch, faktor_um_info, valueOrig, faktoren);
}

export function setKennzahl(standort_tid: string, data: any, kennName: string,
    bereich: string, key: string, field: string, item: any, einheit: string, einheitOrig?: string, gesamtKey?: string, gesamtName?: string,
    faktor_um?: number, faktor_um_quelle?: string, faktor_co2?: number, faktor_co2_quelle?: string, faktor_co2_einheit?: string,
    setHochrechnung?: boolean, faktor_um_einheit?: string, hidden?: boolean, formel_um?: string, formel_co2?: string, formel_hoch?: string,
    faktor_um_info?: string, valueOrig?: number, faktoren?: Array<any>): Kennzahl {
    if (+standort_tid > 0) {
        // Init
        const kenn: Kennzahl = getKennzahl(key, field, item.jahr, standort_tid, data);
        if (!kenn.isInitialized()) {
            kenn
                .setEinheit(einheit)
                .setBereich(bereich)
                .setType(Kennzahl.TYPE_SUMME)
                .setName(`${kennName} [${einheit}]`)
                .setHidden(hidden === true ? true : false);
        }

        // Gesamt
        if (gesamtKey) {
            kenn
                .setGesamt(gesamtKey)
                .setGesamtName(`${gesamtName || `${kennName} Gesamt`} [${einheit}]`);
        }

        // Hochrechnung
        let factor_hochrechnung, jahr_tage, zeitraum_tage;
        if (setHochrechnung) {
            const objHR = getFactorHochrechnung(item.abrechnungszeitraum_von, item.abrechnungszeitraum_bis, item.jahr, true);
            factor_hochrechnung = objHR.factor_hochrechnung;
            jahr_tage = objHR.jahr_tage;
            zeitraum_tage = objHR.zeitraum_tage;
        }

        // Items
        let value = item[field];
        value = value * (faktor_um || 1) * (faktor_co2 || faktor_co2 === 0 ? faktor_co2 : 1) * (factor_hochrechnung || 1);
        const valOri = valueOrig || ((faktor_co2 || faktor_co2 === 0) || faktor_um || factor_hochrechnung ? item[field] : null)
        const kenn_item: Kennzahl = kenn.addValue(value, item.tid);
        kenn_item
            .setType(Kennzahl.TYPE_EINGABE)
            .setEinheitOrig(einheitOrig)
            .setValueOrig(valOri)
            .setDatum(item.abrechnungszeitraum_von, item.abrechnungszeitraum_bis);

        // Faktoren
        // Custom-Faktoren
        if (Array.isArray(faktoren) && faktoren.length > 0) {
            if (valueOrig) {
                kenn_item.setValueOrig(valueOrig);
            } else {
                kenn_item.setValueOrig(value);
            }
            faktoren.forEach((f: any, idx: number) => {
                if (f && f.hasOwnProperty('value') && !kenn_item.hasFaktor(f.type)) {
                    kenn_item
                        .addFaktor(f.value, f.type, f.quelle, f.info, undefined, f.einheit, idx)
                        .setFormel(f.formel);
                }
            });
        }
        // Umrechnung
        if (faktor_um && !kenn_item.hasFaktor(KennzahlFaktor.TYPE_UMRECHNUNG)) {
            const formel = faktor_co2 || faktor_co2 === 0 ? 'umrechnung_co2' : 'umrechnung';
            const info = faktor_um_info || `Umrechnung ${einheitOrig || einheit}`;
            kenn_item
                .addFaktor(faktor_um, KennzahlFaktor.TYPE_UMRECHNUNG, faktor_um_quelle, info, undefined, faktor_um_einheit)
                .setFormel(formel_um || formel);
        }
        // CO2
        if ((faktor_co2 || faktor_co2 === 0) && !kenn_item.hasFaktor(KennzahlFaktor.TYPE_CO2)) {
            const formel = faktor_um ? 'umrechnung_co2' : 'co2_emission';
            kenn_item
                .addFaktor(faktor_co2, KennzahlFaktor.TYPE_CO2, faktor_co2_quelle, 'CO₂-Faktor', undefined, faktor_co2_einheit)
                .setFormel(formel_co2 || formel);
        }
        // Hochrechnung
        if (setHochrechnung && factor_hochrechnung > 0 && factor_hochrechnung !== 1 && !kenn_item.hasFaktor(KennzahlFaktor.TYPE_HOCHRECHNUNG)) {
            const formel = (faktor_co2 || faktor_co2 === 0) && faktor_um ? 'umrechnung_co2_hochrechnung'
                : (faktor_co2 || faktor_co2 === 0) ? 'co2_hochrechnung'
                    : faktor_um ? 'umrechnung_hochrechnung'
                        : 'hochrechnung';
            kenn_item
                .addFaktor(
                    factor_hochrechnung, KennzahlFaktor.TYPE_HOCHRECHNUNG, 'Abrechnungszeitraum', 'Hochrechnung',
                    { tage: { value: jahr_tage, valueOrig: zeitraum_tage + 1 } }
                )
                .setFormel(formel_hoch || formel);
        }

        return kenn_item;
    }
}